import { useQuery } from "@tanstack/react-query";
import { useContext } from "react";

import { ConnectionFailurePage } from "@/components/Error/ConnectionFailurePage";
import { useAccessTokenContext } from "@/providers/AccessTokenProvider";
import { StartupLoading } from "@/providers/StartupLoading";
import { applicationConfigQueryOptions } from "@/queries/configurations/queryOptions";

import { ConfigContext } from "./ConfigContext";

/** Loads the app's anonymous configuration before route-specific user targeting. */
export function ConfigNoUserProvider({ children }: { children: React.ReactNode }): React.ReactNode {
  const { data: snapshot, error } = useQuery(applicationConfigQueryOptions());
  if (!snapshot) {
    return error ? <ConnectionFailurePage /> : <StartupLoading />;
  }

  return <ConfigContext value={{ snapshot }}>{children}</ConfigContext>;
}

/** Evaluates the existing snapshot for the active credential identity, without another async/configuration gate. */
export function ConfigUserProvider({ children }: { children: React.ReactNode }): React.ReactNode {
  const parent = useContext(ConfigContext);
  const { payload, isPending, error } = useAccessTokenContext();
  const identifier = payload?.sub;
  const email = payload?.emails?.[0];
  if (isPending) {
    return <StartupLoading />;
  }
  if (error || !parent || !identifier || !email) {
    return <ConnectionFailurePage />;
  }

  return (
    <ConfigContext value={{ snapshot: parent.snapshot, user: { identifier, email, custom: {} } }}>
      {children}
    </ConfigContext>
  );
}
